// src/components/ListasTrabajo/GestionListas/CrearListaDialog.js

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Grid,
    IconButton,
    Tooltip
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import PaletteIcon from '@mui/icons-material/Palette';

const COLORES_PREDEFINIDOS = [
    { nombre: 'Azul', valor: '#1976d2' },
    { nombre: 'Verde', valor: '#2e7d32' },
    { nombre: 'Rojo', valor: '#d32f2f' },
    { nombre: 'Naranja', valor: '#ed6c02' },
    { nombre: 'Púrpura', valor: '#9c27b0' },
    { nombre: 'Cyan', valor: '#0288d1' },
    { nombre: 'Rosa', valor: '#e91e63' },
    { nombre: 'Ámbar', valor: '#f57c00' },
    { nombre: 'Teal', valor: '#00897b' },
    { nombre: 'Índigo', valor: '#3f51b5' }
];

const CrearListaDialog = ({
    open,
    onClose,
    onGuardar,
    lista = null,
    tipoExpediente
}) => {
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [color, setColor] = useState('#1976d2');
    const [errores, setErrores] = useState({});

    const esEdicion = Boolean(lista);

    // Cargar datos si es edición
    useEffect(() => {
        if (lista) {
            setNombre(lista.Nombre || '');
            setDescripcion(lista.Descripcion || '');
            setColor(lista.Color || '#1976d2');
        } else {
            setNombre('');
            setDescripcion('');
            setColor('#1976d2');
        }
        setErrores({});
    }, [lista, open]);

    const validar = () => {
        const nuevosErrores = {};

        if (!nombre.trim()) {
            nuevosErrores.nombre = 'El nombre es obligatorio';
        } else if (nombre.length > 100) {
            nuevosErrores.nombre = 'El nombre no puede exceder 100 caracteres';
        }

        if (descripcion.length > 500) {
            nuevosErrores.descripcion = 'La descripción no puede exceder 500 caracteres';
        }

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleGuardar = () => {
        if (validar()) {
            onGuardar({
                nombre: nombre.trim(),
                descripcion: descripcion.trim(),
                color,
                tipoExpediente,
                icono: 'folder'
            });
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleGuardar();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { minHeight: 400 } }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {esEdicion ? 'Editar Lista de Trabajo' : 'Nueva Lista de Trabajo'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container direction="column" spacing={3}>
                    {/* Nombre */}
                    <Grid >
                        <TextField
                            fullWidth
                            label="Nombre de la lista"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ej: Casos urgentes, Pendientes de firma..."
                            required
                            autoFocus
                            error={Boolean(errores.nombre)}
                            helperText={errores.nombre || `${nombre.length}/100 caracteres`}
                            inputProps={{ maxLength: 100 }}
                        />
                    </Grid>

                    {/* Descripción */}
                    <Grid >
                        <TextField
                            fullWidth
                            label="Descripción"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            placeholder="Describe el propósito de esta lista..."
                            multiline
                            rows={4}
                            error={Boolean(errores.descripcion)}
                            helperText={errores.descripcion || `${descripcion.length}/500 caracteres`}
                            inputProps={{ maxLength: 500 }}
                        />
                    </Grid>

                    {/* Selector de color */}
                    <Grid >
                        <Typography
                            variant="subtitle2"
                            gutterBottom
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <PaletteIcon style={{ fontSize: 18, marginRight: 6 }} />
                            Color de la lista
                        </Typography>

                        <Grid container spacing={1}>
                            {COLORES_PREDEFINIDOS.map((colorItem) => (
                                <Grid key={colorItem.valor}>
                                    <Tooltip title={colorItem.nombre}>
                                        <IconButton
                                            onClick={() => setColor(colorItem.valor)}
                                            style={{
                                                width: 48,
                                                height: 48,
                                                backgroundColor: colorItem.valor,
                                                border: color === colorItem.valor ? '3px solid' : '1px solid',
                                                borderColor: color === colorItem.valor ? '#1976d2' : 'rgba(0,0,0,0.12)'
                                            }}
                                            size="large" />
                                    </Tooltip>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Preview */}
                    <Grid >
                        <Box
                            style={{
                                padding: 16,
                                borderRadius: 8,
                                backgroundColor: '#fafafa',
                                border: '1px solid rgba(0,0,0,0.12)'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Vista previa:
                            </Typography>

                            <Box display="flex" alignItems="center" style={{ marginTop: 8 }}>
                                <Box
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 8,
                                        backgroundColor: color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        marginRight: 12
                                    }}
                                >
                                    <span aria-hidden="true">📁</span>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                        {nombre || 'Nombre de la lista'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {descripcion || 'Descripción de la lista'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 16, paddingBottom: 16 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleGuardar}
                    variant="contained"
                    disabled={!nombre.trim()}
                    color="primary"
                >
                    {esEdicion ? 'Guardar Cambios' : 'Crear Lista'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CrearListaDialog;
